"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Miscellaneous utility functions.
 */
var utils;
(function (utils) {
    /**
     * Returns the sum of the items in the given array, optionally appying a function to each item.
     * @param {Array} array An array.
     * @param {Function} [f] An optional function that maps each array item to a number.
     * @returns {number} The sum of the array items, after applying the function f if provided.
     */
    function sum(array, f) {
        return array.reduce((s, item, index) => s + (f ? f(item, index) : item), 0);
    }
    utils.sum = sum;
    /**
     * Returns the average of the items in the given array, optionally appying a function to each
     * item.
     * @param {Array} array An array.
     * @param {Function} [f] An optional function that maps each array item to a number.
     * @returns {number} The average of the array items, after applying the function f if provided.
     */
    function average(array, f) {
        return utils.sum(array, f) / Math.max(array.length, 1);
    }
    utils.average = average;
    /**
     * Returns the number of items in the given array for which the specified predicate returns
     * true.
     * @param {Array} array An array.
     * @param {Function} predicate A predicate.
     * @returns {number} The number of items in the array for which the predicate returns true.
     */
    function countIf(array, predicate) {
        return utils.sum(array, (item, index) => (predicate(item, index) ? 1 : 0));
    }
    utils.countIf = countIf;
    /**
     * Returns the ith cyclic ordered number from start in the range [0, n - 1].
     * @param {number} start A non-negative integer representing the current index.
     * @param {number} n A non-negative integer representing the length of an array.
     * @param {number} [i = 1] An integer representing increment amount.
     * @returns {number} A non-negative integer representing the ith next index.
     */
    function getNextIndex(start, n, i = 1) {
        if (n === 0) {
            throw new Error('Cannot increment index of array of length 0.');
        }
        if (start < 0) {
            throw new Error('Starting index must be positive.');
        }
        if (n < 0) {
            throw new Error('Array length must be positive.');
        }
        if (start % 1 !== 0 || n % 1 !== 0 || i % 1 !== 0) {
            throw new Error('Parameters must be integers.');
        }
        const index = start + i;
        return ((index % n) + n) % n;
    }
    utils.getNextIndex = getNextIndex;
    /**
     * Returns true if the given value is a number.
     * @param {any} value The value to check.
     * @returns {boolean} True if the given value is a number, false otherwise.
     */
    function isNumber(value) {
        return typeof value === 'number';
    }
    utils.isNumber = isNumber;
    /**
     * Removes the first instance of the specified item from the given array.
     * @param {Array} array An array.
     * @param {any} item An item that may be in the array.
     * @returns {boolean} True if the item was removed; false, otherwise.
     */
    function removeItemFromArray(array, item) {
        const index = array.indexOf(item);
        if (index >= 0) {
            array.splice(index, 1);
            return true;
        }
        return false;
    }
    utils.removeItemFromArray = removeItemFromArray;
    /**
     * A comparison function that allows `Array.sort` to sort an array numerically instead of using
     * the default string comparison.
     * @param {number} a The first number to compare.
     * @param {number} b The second number to compare.
     * @returns {number} A positive number if a is larger than b, a negative number if b is larger
     * than a, or zero if they are equal.
     * @see {@link https://stackoverflow.com/questions/1063007/how-to-sort-an-array-of-integers-correctly}
     */
    function sortNumberCompare(a, b) {
        return a - b;
    }
    utils.sortNumberCompare = sortNumberCompare;
    /**
     * Clamps a value between a min and max.
     * @param {number} val The value to clamp.
     * @param {number} min The min clamp range.
     * @param {number} max The max clamp range.
     * @returns {number} A clamped value.
     */
    function clamp(val, min, max) {
        if (min > max) {
            throw new Error('Min param must be less than or equal to max param.');
        }
        return Math.max(min, Math.min(max, val));
    }
    utils.clamp = clamp;
    /**
     * Converts an angle from radians to degrees.
     * @param {number} rad The value in radians.
     * @returns {number} The value in degrees.
     */
    function convertToDegrees(rad) {
        return (rad * 180) / Math.PI;
    }
    utils.convertToDegrees = convertToDegrees;
    /**
     * Converts an angle from degrees to radians.
     * @param {number} deg The value in degrees.
     * @returns {number} The value in radians.
     */
    function convertToRadians(deg) {
        return (deg * Math.PI) / 180;
    }
    utils.convertToRadians = convertToRadians;
})(utils = exports.utils || (exports.utils = {}));
//# sourceMappingURL=utils.js.map